import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Tilt — cinematic 3D tilt-on-hover wrapper.
 * Inspired by HeyGen / Apple product cards.
 *
 * Usage:
 *   <Tilt><div className="rounded-2xl ...">card content</div></Tilt>
 *
 * Props:
 *   intensity   — max tilt in degrees (default 14)
 *   scale       — hover scale (default 1.04)
 *   glare       — boolean: render light highlight that follows the cursor
 *   className   — applied to the outer wrapper
 */
export default function Tilt({
  children,
  intensity = 14,
  scale = 1.04,
  glare = true,
  className = "",
  testId,
}) {
  const ref = useRef(null);

  // Raw motion values (-0.5 .. 0.5)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  // Smooth springs
  const sx = useSpring(mx, { stiffness: 220, damping: 22, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 220, damping: 22, mass: 0.6 });

  // Map to rotation
  const rotateX = useTransform(sy, [-0.5, 0.5], [intensity, -intensity]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-intensity, intensity]);

  // Glare position
  const glareX = useTransform(sx, [-0.5, 0.5], ["20%", "80%"]);
  const glareY = useTransform(sy, [-0.5, 0.5], ["20%", "80%"]);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(x);
    my.set(y);
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      data-testid={testId}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileHover={{ scale }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      style={{
        rotateX,
        rotateY,
        transformPerspective: 1100,
        transformStyle: "preserve-3d",
      }}
      className={`relative will-change-transform ${className}`}
    >
      {children}
      {glare && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at ${glareX.get?.() ?? "50%"} ${glareY.get?.() ?? "50%"}, rgba(255,255,255,0.18), transparent 55%)`,
            mixBlendMode: "soft-light",
          }}
        />
      )}
    </motion.div>
  );
}
